import axios from 'axios';
import {ChangeEvent, useCallback, useEffect, useRef, useState} from 'react';
import {
  ProfileApiHomeHeaderAppearance,
  profileApi,
} from '../../../../services/profileApi';
import {
  DEFAULT_HEADER_THEME,
  DEFAULT_IMAGE_POSITION,
  DEFAULT_IMAGE_ZOOM,
  HEADER_THEME_EVENT_NAME,
  HeaderThemeId,
  HeaderThemeSelection,
  ImagePosition,
  MAX_HEADER_IMAGE_SIZE_BYTES,
} from '../constants';

const SUPPORTED_HEADER_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const createDefaultAppearance = (): ProfileApiHomeHeaderAppearance => ({
  theme: DEFAULT_HEADER_THEME,
  imagePosition: {...DEFAULT_IMAGE_POSITION},
  imageZoom: DEFAULT_IMAGE_ZOOM,
});

const normalizeAppearance = (
  appearance?: ProfileApiHomeHeaderAppearance
): ProfileApiHomeHeaderAppearance => ({
  theme: appearance?.theme || DEFAULT_HEADER_THEME,
  customImageUrl: appearance?.customImageUrl || undefined,
  customImageObjectKey: appearance?.customImageObjectKey || undefined,
  imagePosition: {
    x: typeof appearance?.imagePosition?.x === 'number'
      ? appearance.imagePosition.x
      : DEFAULT_IMAGE_POSITION.x,
    y: typeof appearance?.imagePosition?.y === 'number'
      ? appearance.imagePosition.y
      : DEFAULT_IMAGE_POSITION.y,
  },
  imageZoom: typeof appearance?.imageZoom === 'number'
    ? appearance.imageZoom
    : DEFAULT_IMAGE_ZOOM,
  updatedAt: appearance?.updatedAt,
});

const dispatchHeaderThemeChange = (appearance: ProfileApiHomeHeaderAppearance) => {
  window.dispatchEvent(new CustomEvent(HEADER_THEME_EVENT_NAME, {detail: appearance}));
};

const readFileAsDataUrl = (file: File) => new Promise<string>((resolve, reject) => {
  const reader = new FileReader();

  reader.onloadend = () => {
    if (typeof reader.result === 'string') {
      resolve(reader.result);
      return;
    }

    reject(new Error('Failed to read the selected image.'));
  };

  reader.onerror = () => {
    reject(new Error('Failed to read the selected image.'));
  };

  reader.readAsDataURL(file);
});

export const useHeaderAppearanceSettings = (token: string | null) => {
  const requestControllerRef = useRef<AbortController | null>(null);

  const [persistedAppearance, setPersistedAppearance] = useState<ProfileApiHomeHeaderAppearance>(createDefaultAppearance());
  const [headerTheme, setHeaderTheme] = useState<HeaderThemeSelection>(DEFAULT_HEADER_THEME);
  const [customHeaderImage, setCustomHeaderImage] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [imagePosition, setImagePosition] = useState<ImagePosition>({...DEFAULT_IMAGE_POSITION});
  const [imageZoom, setImageZoom] = useState(DEFAULT_IMAGE_ZOOM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const abortRequest = useCallback(() => {
    requestControllerRef.current?.abort();
    requestControllerRef.current = null;
  }, []);

  const createRequestSignal = useCallback(() => {
    abortRequest();
    const controller = new AbortController();
    requestControllerRef.current = controller;
    return controller.signal;
  }, [abortRequest]);

  const applyAppearanceState = useCallback((appearance: ProfileApiHomeHeaderAppearance) => {
    const normalizedAppearance = normalizeAppearance(appearance);

    setPersistedAppearance(normalizedAppearance);
    setHeaderTheme(normalizedAppearance.theme);
    setCustomHeaderImage(normalizedAppearance.customImageUrl || '');
    setImagePreview(normalizedAppearance.customImageUrl || '');
    setImagePosition({...normalizedAppearance.imagePosition});
    setImageZoom(normalizedAppearance.imageZoom);
    setSelectedImageFile(null);

    return normalizedAppearance;
  }, []);

  const restorePersistedAppearance = useCallback(() => {
    setHeaderTheme(persistedAppearance.theme);
    setCustomHeaderImage(persistedAppearance.customImageUrl || '');
    setImagePreview(persistedAppearance.customImageUrl || '');
    setImagePosition({...persistedAppearance.imagePosition});
    setImageZoom(persistedAppearance.imageZoom);
    setSelectedImageFile(null);
  }, [persistedAppearance]);

  useEffect(() => {
    if (!token) {
      const defaultAppearance = createDefaultAppearance();
      applyAppearanceState(defaultAppearance);
      setError('You must be signed in to customize the header appearance.');
      setLoading(false);
      return;
    }

    let isMounted = true;
    const requestSignal = createRequestSignal();

    const loadAppearance = async () => {
      try {
        setLoading(true);
        setError(null);

        const appearance = await profileApi.getMyHomeHeaderAppearance(token, requestSignal);

        if (!isMounted || requestSignal.aborted) {
          return;
        }

        applyAppearanceState(appearance);
      } catch (loadError) {
        if ((axios.isAxiosError(loadError) && loadError.code === 'ERR_CANCELED') || requestSignal.aborted || !isMounted) {
          return;
        }

        applyAppearanceState(createDefaultAppearance());
        setError(loadError instanceof Error ? loadError.message : 'Failed to load header appearance');
      } finally {
        if (isMounted && !requestSignal.aborted) {
          setLoading(false);
        }

        if (requestControllerRef.current?.signal === requestSignal) {
          requestControllerRef.current = null;
        }
      }
    };

    void loadAppearance();

    return () => {
      isMounted = false;
      abortRequest();
    };
  }, [abortRequest, applyAppearanceState, createRequestSignal, token]);

  const handleThemeChange = useCallback(async (theme: HeaderThemeId) => {
    if (!token || loading || saving) {
      return;
    }

    const requestSignal = createRequestSignal();

    try {
      setSaving(true);
      setError(null);

      const appearance = await profileApi.updateMyHomeHeaderTheme(token, theme, requestSignal);

      if (requestSignal.aborted) {
        return;
      }

      const normalizedAppearance = applyAppearanceState(appearance);
      setShowImageEditor(false);
      dispatchHeaderThemeChange(normalizedAppearance);
    } catch (saveError) {
      if ((axios.isAxiosError(saveError) && saveError.code === 'ERR_CANCELED') || requestSignal.aborted) {
        return;
      }

      setError(saveError instanceof Error ? saveError.message : 'Failed to save header theme');
    } finally {
      if (!requestSignal.aborted) {
        setSaving(false);
      }

      if (requestControllerRef.current?.signal === requestSignal) {
        requestControllerRef.current = null;
      }
    }
  }, [applyAppearanceState, createRequestSignal, loading, saving, token]);

  const handleImageUpload = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file || saving) {
      return;
    }

    if (!SUPPORTED_HEADER_IMAGE_TYPES.includes(file.type)) {
      setError('Only JPG, PNG, and WEBP images are supported.');
      return;
    }

    if (file.size > MAX_HEADER_IMAGE_SIZE_BYTES) {
      setError('Image size must be 5MB or less.');
      return;
    }

    try {
      const imageData = await readFileAsDataUrl(file);
      setSelectedImageFile(file);
      setCustomHeaderImage(imageData);
      setImagePreview(imageData);
      setHeaderTheme('custom');
      setImagePosition({...DEFAULT_IMAGE_POSITION});
      setImageZoom(DEFAULT_IMAGE_ZOOM);
      setError(null);
      setShowImageEditor(true);
    } catch (readError) {
      setError(readError instanceof Error ? readError.message : 'Failed to read the selected image');
    }
  }, [saving]);

  const handleSaveImageSettings = useCallback(async () => {
    if (!token || saving) {
      return;
    }

    if (!selectedImageFile && !persistedAppearance.customImageUrl) {
      setError('Upload a custom header image before saving.');
      return;
    }

    const requestSignal = createRequestSignal();

    try {
      setSaving(true);
      setError(null);

      const appearance = await profileApi.updateMyHomeHeaderCustomImage(token, {
        file: selectedImageFile,
        imagePosition,
        imageZoom,
      }, requestSignal);

      if (requestSignal.aborted) {
        return;
      }

      const normalizedAppearance = applyAppearanceState(appearance);
      setShowImageEditor(false);
      dispatchHeaderThemeChange(normalizedAppearance);
    } catch (saveError) {
      if ((axios.isAxiosError(saveError) && saveError.code === 'ERR_CANCELED') || requestSignal.aborted) {
        return;
      }

      setError(saveError instanceof Error ? saveError.message : 'Failed to save custom header image');
    } finally {
      if (!requestSignal.aborted) {
        setSaving(false);
      }

      if (requestControllerRef.current?.signal === requestSignal) {
        requestControllerRef.current = null;
      }
    }
  }, [
    applyAppearanceState,
    createRequestSignal,
    imagePosition,
    imageZoom,
    persistedAppearance.customImageUrl,
    saving,
    selectedImageFile,
    token,
  ]);

  const handleCancelImageEdit = useCallback(() => {
    if (saving) {
      return;
    }

    restorePersistedAppearance();
    setError(null);
    setShowImageEditor(false);
  }, [restorePersistedAppearance, saving]);

  const handleRemoveCustomImage = useCallback(async () => {
    if (!token || loading || saving) {
      return;
    }

    const requestSignal = createRequestSignal();

    try {
      setSaving(true);
      setError(null);

      const appearance = await profileApi.removeMyHomeHeaderCustomImage(token, requestSignal);

      if (requestSignal.aborted) {
        return;
      }

      const normalizedAppearance = applyAppearanceState(appearance);
      setShowImageEditor(false);
      dispatchHeaderThemeChange(normalizedAppearance);
    } catch (removeError) {
      if ((axios.isAxiosError(removeError) && removeError.code === 'ERR_CANCELED') || requestSignal.aborted) {
        return;
      }

      setError(removeError instanceof Error ? removeError.message : 'Failed to remove custom header image');
    } finally {
      if (!requestSignal.aborted) {
        setSaving(false);
      }

      if (requestControllerRef.current?.signal === requestSignal) {
        requestControllerRef.current = null;
      }
    }
  }, [applyAppearanceState, createRequestSignal, loading, saving, token]);

  useEffect(() => () => {
    abortRequest();
  }, [abortRequest]);

  return {
    headerTheme,
    customHeaderImage,
    imagePreview,
    showImageEditor,
    imagePosition,
    imageZoom,
    loading,
    saving,
    error,
    setShowImageEditor,
    setImagePosition,
    setImageZoom,
    handleThemeChange,
    handleImageUpload,
    handleSaveImageSettings,
    handleCancelImageEdit,
    handleRemoveCustomImage,
  };
};
